import React, { useEffect, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Link,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import SignupShell from './signupShell';
import signupTheme, { authHeadingStyles, authSubtextStyles } from './signupTheme';
import { step4Schema, step5Schema } from './validationSchemas';
import { PHONE_MAX_LENGTH } from '../../constants/phone';
import { CountryPhoneCodeMenuOption, CountryPhoneCodeOption } from './countryFlag';
import { getCountryMetaData } from './login.service';
import { orderCountries, readBrowserSignals, resolveDefaultCountry } from './loginCountry';
import { login as loginApi, loginHandover, loginSentOtp, pinLogin, pinSendOtp, pinSet, pinVerifyOtp } from '../../services/authServices';
import PinEntryStep from './pin/PinEntryStep';
import PinCreateStep from './pin/PinCreateStep';
import LoginChooser from './chooser/LoginChooser';
import { isPinLoginEnabled } from './pin/pinFeatureFlag';
import { GenericConstants } from '../constants/genericConstant';

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};

const fieldGroupStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};
const OTP_LENGTH = 4;

// Minimum delay between "Send OTP" / "Resend OTP" taps. Every tap is a paid
// Twilio message, so an impatient user tapping four times costs four texts for
// one login. Matches the cooldown the agent and farmer mobile apps already use.
const RESEND_COOLDOWN_SECONDS = 30;

// PIN login (feature/pin-login). The backend answers the phone step with data.nextStep: PIN (no SMS),
// PIN_SETUP or PIN_RESET (an OTP was sent, then a new PIN), or nothing for today's OTP login.
// Screens: PHONE -> OTP | PIN | PIN_CREATE -> CHOOSER. otpMode says what a verified OTP leads to.
// CHOOSER is the page after sign-in: the account's apps plus eKutir's news and recommendations.
const STEP = { PHONE: 'PHONE', OTP: 'OTP', PIN: 'PIN', PIN_CREATE: 'PIN_CREATE', CHOOSER: 'CHOOSER' };

// Leave a margin before the sign-in's authorization code expires: the destination app still has to
// load and exchange it.
const CODE_EXPIRY_MARGIN_MS = 15000;
const OTP_MODE = { LOGIN: 'LOGIN', PIN_SETUP: 'PIN_SETUP', PIN_RESET: 'PIN_RESET' };

const otpGridWidth = {
  xs: 'calc(4 * 56px + 3 * 12px)',
  sm: 'calc(4 * 72px + 3 * 20px)',
};

const renderFieldLabel = (label, required = false) => (
  <Typography variant="subtitle2" sx={fieldLabelStyles}>
    {label}
    {required ? (
      <Box component="span" sx={{ color: 'error.main' }}>
        {' '}
        *
      </Box>
    ) : null}
  </Typography>
);

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { LOGIN_TEXT } = GenericConstants();
  const [countryData, setCountryData] = useState([]);
  // The country list could not be loaded, so sign-in cannot proceed. Kept apart from
  // `errors`, which is per-field validation the user can act on — this one is not their
  // fault and not fixable by typing.
  const [metadataFailed, setMetadataFailed] = useState(false);
  const [errors, setErrors] = useState({});
  const [sentOtpData, setSentOtpData] = useState({
    phoneNumber: '',
    countryCode: '',
  });
  const [step, setStep] = useState(STEP.PHONE);
  const isOtpSent = step === STEP.OTP;
  const [otpMode, setOtpMode] = useState(OTP_MODE.LOGIN);
  // Set once the user picks "Sign in with a code instead": resends must not ask for PIN again.
  const [forceOtp, setForceOtp] = useState(false);
  const [pinInfo, setPinInfo] = useState({ otpFallback: false, attemptsLeft: null, lockedUntil: null });
  const [pinTicket, setPinTicket] = useState('');
  const [pinCreateError, setPinCreateError] = useState('');
  // The signed-in account's apps and page cards, and the code for the default app, while CHOOSER shows.
  const [chooser, setChooser] = useState(null);
  const [otpDigits, setOtpDigits] = useState(() => Array(OTP_LENGTH).fill(''));
  const [feedback, setFeedback] = useState(null);
  // Closing keeps the message and severity, so the fade-out shows this alert, not an empty
  // blue 'info' one.
  const closeFeedback = () => setFeedback((current) => current && { ...current, open: false });
  const [loading, setLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;

    const interval = setInterval(() => {
      setResendSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [resendSeconds]);

  // The country list, and the sign-in that silently depended on it.
  //
  // `countryCode` starts empty and is only ever set from this call, so a failure here used
  // to leave the form quietly unusable: the combobox had no options, `step4Schema` refused
  // the submit with "Country Code is required", and NO OTP WAS EVER REQUESTED. The user saw
  // a complaint about a field they had not touched, on a form that looked ready. A single
  // blip on this one GET took the whole front door down — and eK Link is the front door for
  // every application in the estate.
  //
  // So: retry a couple of times, and when it still will not load, say so and stop the form
  // pretending it can be submitted. A default dial code would not help on its own — the
  // dropdown would still be empty and the user could not change it.
  useEffect(() => {
    let cancelled = false;

    const loadCountries = async () => {
      setMetadataFailed(false);

      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (cancelled) return;

        const res = await getCountryMetaData();

        if (res.statusCode === 200 && Array.isArray(res.data) && res.data.length > 0) {
          if (cancelled) return;

          const countryManagementList = orderCountries(res.data.map((item) => item.countryManagement));
          setCountryData(countryManagementList);
          const defaultCountry = resolveDefaultCountry(countryManagementList, {
            search: location.search,
            browser: readBrowserSignals(),
          });

          // Check if phone is pre-filled from signup
          const prefillPhone = location.state?.phoneNumber;
          const prefillCountry = location.state?.countryCode;
          const prefillMessage = location.state?.message;

          if (prefillMessage) {
            showFeedback('success', prefillMessage);
          }

          // Never overwrite what the user has already typed.
          //
          // This effect used to resolve in one round trip, so the window between the form
          // appearing and this running was too small to notice. Retrying widened it to
          // seconds — and the old code reset `phoneNumber` unconditionally, so a number
          // entered while the countries were still loading was silently wiped and the form
          // then refused the submit with "Please enter a valid phone number" about a field
          // the user had definitely filled in.
          setSentOtpData((prev) => ({
            phoneNumber: prev.phoneNumber || prefillPhone || '',
            countryCode:
              prev.countryCode
              || prefillCountry
              || (defaultCountry ? String(defaultCountry.phoneCode) : ''),
          }));
          return;
        }

        // Backing off rather than hammering: this runs on every load of the busiest page in
        // the estate, and the failure it is recovering from is usually the backend being
        // briefly overloaded.
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      }

      if (!cancelled) {
        setMetadataFailed(true);
      }
    };

    loadCountries();

    return () => {
      cancelled = true;
    };
  }, [location.state, location.search]);

  const showFeedback = (severity, message) => {
    setFeedback({ severity, message, open: true });
  };

  const handleChangeSentOtp = (event) => {
    const { name, value } = event.target;
    // Phone length is uniform across the platform (digits only, fixed range).
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    setSentOtpData((currentData) => ({
      ...currentData,
      [name]: name === 'phoneNumber' ? value.replace(/\D/g, '').slice(0, PHONE_MAX_LENGTH) : value,
    }));
  };

  const phoneLabel = `${sentOtpData.countryCode} ${sentOtpData.phoneNumber}`.trim();

  const openOtpStep = (mode, message = 'OTP sent successfully.') => {
    setOtpMode(mode);
    setOtpDigits(Array(OTP_LENGTH).fill(''));
    setStep(STEP.OTP);
    setResendSeconds(RESEND_COOLDOWN_SECONDS);
    showFeedback('success', message);
  };

  // Where the phone step's answer leads. No nextStep (an older backend, or a persona on OTP) is
  // today's OTP login.
  const followNextStep = (response) => {
    const nextStep = response.data?.nextStep;
    if (nextStep === 'PIN') {
      setPinInfo({
        otpFallback: Boolean(response.data?.otpFallback),
        attemptsLeft: null,
        lockedUntil: response.data?.lockedUntil || null,
      });
      setStep(STEP.PIN);
    } else if (nextStep === OTP_MODE.PIN_SETUP) {
      openOtpStep(OTP_MODE.PIN_SETUP, 'We sent you a code. Verify your phone, then create your PIN.');
    } else if (nextStep === OTP_MODE.PIN_RESET) {
      openOtpStep(OTP_MODE.PIN_RESET, 'Your PIN needs to be reset. We sent you a code.');
    } else {
      openOtpStep(OTP_MODE.LOGIN);
    }
  };

  const requestLoginOtp = async ({ offerPin }) => {
    const payload = {
      phoneNumber: sentOtpData.phoneNumber,
      countryCode: sentOtpData.countryCode,
    };
    if (offerPin) {
      payload.capabilities = ['PIN'];
    }
    return loginSentOtp(payload);
  };

  const handleSentOtp = async (event) => {
    event.preventDefault();
    if (resendSeconds > 0) return;

    try {
      await step4Schema.validate(
        { ...sentOtpData },
        { abortEarly: false }
      );
      setErrors({});

      setLoading(true);
      const response = await requestLoginOtp({ offerPin: isPinLoginEnabled() && !forceOtp });

      if (response.statusCode === 200) {
        followNextStep(response);
        setLoading(false);
      } else {
        showFeedback('error', response.message || 'Unable to send OTP right now.');
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      if (err.inner) {
        const formattedErrors = err.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});
        setErrors(formattedErrors);
      } else {
        showFeedback('error', 'Unable to send OTP right now.');
      }
    }
  };

  const handleCountryChange = (event) => {
    const phoneCode = event.target.value;
    setErrors((prevErrors) => ({ ...prevErrors, countryCode: '' }));
    setSentOtpData((prev) => ({ ...prev, countryCode: phoneCode }));
  };

  const getSelectedCountry = () =>
    countryData.find(
      (country) => String(country.phoneCode) === String(sentOtpData.countryCode)
    );

  // The end of every successful sign-in, OTP or PIN: both answer with data.user.personaAppUrl and a
  // one-time authorization code the destination app exchanges for tokens.
  const redirectWithHandover = (response) => {
    const personaAppUrl = response.data?.user?.personaAppUrl;
    const accessToken = response.data?.accessToken;
    const refreshToken = response.data?.refreshToken;
    const authCode = response.data?.authCode;
    const userId = response.data?.user?.id;

    // Store tokens in localStorage if provided (backward compatibility)
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    if (!personaAppUrl) {
      // No API URL configured - return to login immediately
      setLoading(false);
      showFeedback('error', 'Configuration error: No application URL found. Please contact support.');
      return;
    }

    showFeedback('success', 'Login successful.');
    setTimeout(() => {
      if (isExternalUrl(personaAppUrl)) {
        // External app redirect - prefer authorization code for secure inter-service token exchange
        const params = new URLSearchParams();
        if (authCode && userId) {
          // New secure flow: pass authorization code
          params.append('code', authCode);
          params.append('userId', userId);
        } else if (accessToken) {
          // Fallback to old flow if code not available
          params.append('accessToken', accessToken);
          if (refreshToken) {
            params.append('refreshToken', refreshToken);
          }
        }
        const tokenParam = params.toString() ? `?${params.toString()}` : '';
        const redirectUrlWithToken = `${personaAppUrl}${tokenParam}`;
        window.location.replace(redirectUrlWithToken);
      } else {
        // Internal route - navigate
        navigate(personaAppUrl, { replace: true });
      }
    }, 1500);
  };

  const isExternalUrl = (url) => url.startsWith('http://') || url.startsWith('https://');

  // Opens an app with a one-time authorization code it exchanges for its own tokens.
  const goToApp = (appUrl, authCode, userId) => {
    if (!isExternalUrl(appUrl)) {
      navigate(appUrl, { replace: true });
      return;
    }
    const params = new URLSearchParams({ code: authCode, userId });
    window.location.replace(`${appUrl}?${params.toString()}`);
  };

  // The end of every successful sign-in, OTP or PIN: the page listing the account's apps. A backend
  // that sends no list (one older than the page) still gets today's straight redirect.
  const finishSignIn = (response) => {
    const destinations = response.data?.destinations;
    if (!Array.isArray(destinations) || destinations.length === 0 || !response.data?.authCode) {
      redirectWithHandover(response);
      return;
    }
    setLoading(false);
    setChooser({
      destinations,
      highlights: Array.isArray(response.data?.highlights) ? response.data.highlights : [],
      authCode: response.data.authCode,
      authCodeExpiresAt: response.data.authCodeExpiresAt ? Date.parse(response.data.authCodeExpiresAt) : null,
      userId: response.data.user?.id,
      userName: response.data.user?.fullName || response.data.user?.name || '',
      defaultUrl: response.data.user?.personaAppUrl,
    });
    setStep(STEP.CHOOSER);
  };

  const signInAgain = (message) => {
    setChooser(null);
    handleChangeNumber();
    showFeedback('warning', message);
  };

  // "Log out" on the page after sign-in: forget the session this page holds and go back to the
  // eK Link front door. The sign-in's unused authorization code expires on its own.
  const handleLogout = () => {
    ['accessToken', 'refreshToken', 'userData', 'tokenDomain'].forEach((key) => localStorage.removeItem(key));
    setChooser(null);
    handleChangeNumber();
    setSentOtpData((prev) => ({ ...prev, phoneNumber: '' }));
    navigate('/login', { replace: true });
  };

  // A button on the page after sign-in. The sign-in's code is for the default app; any other app
  // swaps it at the backend for a code for that app.
  const handleOpenDestination = async (destination) => {
    if (!chooser) return;
    if (chooser.authCodeExpiresAt && Date.now() > chooser.authCodeExpiresAt - CODE_EXPIRY_MARGIN_MS) {
      signInAgain('Your sign-in has expired. Please sign in again.');
      return;
    }
    setLoading(true);
    if (destination.appUrl === chooser.defaultUrl) {
      goToApp(destination.appUrl, chooser.authCode, chooser.userId);
      return;
    }
    const response = await loginHandover({
      code: chooser.authCode,
      userId: chooser.userId,
      destinationId: destination.id,
    });
    if (response.statusCode === 200 && response.data?.authCode) {
      goToApp(response.data.user?.personaAppUrl || destination.appUrl, response.data.authCode, chooser.userId);
      return;
    }
    setLoading(false);
    if (/expired/i.test(response.message || '')) {
      signInAgain(response.message);
    } else {
      showFeedback('error', response.message || 'Unable to open that application right now.');
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    const otpValue = otpDigits.join('');
    const otpData = {
      otp: otpValue,
      phoneNumber: sentOtpData.phoneNumber,
      countryCode: sentOtpData.countryCode,
    };

    try {
      await step5Schema.validate(otpData, { abortEarly: false });
      setErrors({});

      setLoading(true);

      if (otpMode !== OTP_MODE.LOGIN) {
        // PIN setup or reset: a verified OTP earns a single-use ticket for the new PIN, not a login.
        const verified = await pinVerifyOtp({ ...otpData, purpose: otpMode });
        setLoading(false);
        if (verified.statusCode === 200 && verified.pinTicket) {
          setPinTicket(verified.pinTicket);
          setPinCreateError('');
          setStep(STEP.PIN_CREATE);
        } else {
          showFeedback('error', verified.message || 'Unable to verify OTP right now.');
        }
        return;
      }

      const response = await loginApi(otpData);

      if (response.statusCode === 200) {
        finishSignIn(response);
      } else {
        showFeedback('error', response.message || 'Unable to verify OTP right now.');
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      if (err.inner) {
        const formattedErrors = err.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});
        setErrors(formattedErrors);
      } else {
        showFeedback('error', 'Unable to verify OTP right now.');
      }
    }
  };

  // "Resend OTP": the same purpose the code was sent for.
  const handleResendOtp = async (event) => {
    if (otpMode === OTP_MODE.LOGIN) {
      handleSentOtp(event);
      return;
    }
    event.preventDefault();
    if (resendSeconds > 0) return;
    await startPinOtp(otpMode);
  };

  // An OTP for first setup or a forgotten PIN, from the PIN screen.
  const startPinOtp = async (purpose) => {
    setLoading(true);
    const response = await pinSendOtp({
      phoneNumber: sentOtpData.phoneNumber,
      countryCode: sentOtpData.countryCode,
      purpose,
    });
    setLoading(false);
    if (response.statusCode === 200) {
      openOtpStep(purpose);
    } else {
      showFeedback('error', response.message || 'Unable to send OTP right now.');
    }
  };

  // "Sign in with a code instead": today's OTP login, without offering PIN.
  const handleUseOtp = async () => {
    setForceOtp(true);
    setLoading(true);
    const response = await requestLoginOtp({ offerPin: false });
    setLoading(false);
    if (response.statusCode === 200) {
      openOtpStep(OTP_MODE.LOGIN);
    } else {
      showFeedback('error', response.message || 'Unable to send OTP right now.');
    }
  };

  const handleChangeNumber = () => {
    setStep(STEP.PHONE);
    setForceOtp(false);
    setPinInfo({ otpFallback: false, attemptsLeft: null, lockedUntil: null });
    setResendSeconds(0);
  };

  const handlePinLogin = async (pin) => {
    setLoading(true);
    const response = await pinLogin({
      phoneNumber: sentOtpData.phoneNumber,
      countryCode: sentOtpData.countryCode,
      pin,
    });
    if (response.statusCode === 200) {
      finishSignIn(response);
      return;
    }
    setLoading(false);
    const nextStep = response.data?.nextStep;
    if (response.statusCode === 401) {
      setPinInfo((current) => ({ ...current, attemptsLeft: response.data?.attemptsLeft ?? null }));
      showFeedback('error', response.message || 'Wrong PIN.');
    } else if (response.statusCode === 423) {
      setPinInfo((current) => ({ ...current, attemptsLeft: null, lockedUntil: response.data?.lockedUntil || null }));
    } else if (nextStep === OTP_MODE.PIN_RESET || nextStep === OTP_MODE.PIN_SETUP) {
      showFeedback('info', response.message);
      await startPinOtp(nextStep);
    } else if (nextStep === 'OTP') {
      // The persona went back to OTP since the phone step: carry on with today's login.
      await handleUseOtp();
    } else {
      showFeedback('error', response.message || 'Unable to sign in right now.');
    }
  };

  const handlePinCreate = async (pin, pinConfirm) => {
    setLoading(true);
    setPinCreateError('');
    const response = await pinSet({ pinTicket, pin, pinConfirm });
    if (response.statusCode === 200) {
      finishSignIn(response);
      return;
    }
    setLoading(false);
    setPinCreateError(response.message || 'Unable to save your PIN right now.');
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    setErrors((prevErrors) => ({ ...prevErrors, otp: '' }));

    setOtpDigits((currentOtp) => {
      const nextOtp = [...currentOtp];
      nextOtp[index] = value;
      return nextOtp;
    });

    if (value && index < OTP_LENGTH - 1) {
      const nextInput = document.getElementById(`login-otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const previousInput = document.getElementById(`login-otp-${index - 1}`);
      previousInput?.focus();
    }
  };

  return (
    <ThemeProvider theme={signupTheme}>
      <CssBaseline />
      <SignupShell
        activeTab="login"
        onNavigateSignUp={() => navigate('/register')}
        onNavigateLogin={() => navigate('/login')}
        signedInAs={step === STEP.CHOOSER && chooser ? chooser.userName : undefined}
        onLogout={handleLogout}
      >
        {step === STEP.CHOOSER && chooser ? (
          <LoginChooser
            userName={chooser.userName}
            destinations={chooser.destinations}
            highlights={chooser.highlights}
            busy={loading}
            onOpen={handleOpenDestination}
          />
        ) : (
        <Box
          sx={{
            maxWidth: 760,
            mx: 'auto',
            minHeight: { xs: 'auto', md: 'calc(100vh - 220px)' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 560,
              mx: 'auto',
              px: { xs: 2.5, sm: 6, md: 8 },
              py: { xs: 3, sm: 6, md: 8 },
              minHeight: { xs: 'auto', md: 560 },
              borderRadius: 4,
              backgroundColor: '#ffffff',
              boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
              border: '1px solid #E4E6F1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
              <Box sx={{ width: '100%', maxWidth: 440, mx: 'auto' }}>
              <Stack spacing={{ xs: 3.5, sm: 4.5 }}>
                <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ pb: { xs: 1, sm: 2 } }}>
                  <Typography
                    variant="h4"
                    sx={authHeadingStyles}
                  >
                    {LOGIN_TEXT.HEADER}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={authSubtextStyles}
                  >
                    {LOGIN_TEXT.SUB_HEADER}
                  </Typography>
                </Stack>

                {step === STEP.PIN ? (
                  <PinEntryStep
                    phoneLabel={phoneLabel}
                    attemptsLeft={pinInfo.attemptsLeft}
                    lockedUntil={pinInfo.lockedUntil}
                    otpFallback={pinInfo.otpFallback}
                    onSubmit={handlePinLogin}
                    onForgot={() => startPinOtp(OTP_MODE.PIN_RESET)}
                    onUseOtp={handleUseOtp}
                    onChangeNumber={handleChangeNumber}
                  />
                ) : null}

                {step === STEP.PIN_CREATE ? (
                  <PinCreateStep
                    mode={otpMode}
                    phoneNumber={sentOtpData.phoneNumber}
                    serverError={pinCreateError}
                    onSubmit={handlePinCreate}
                  />
                ) : null}

                {step === STEP.PHONE || step === STEP.OTP ? (
                <form onSubmit={isOtpSent ? handleVerifyOtp : handleSentOtp}>
                  <Stack spacing={{ xs: 3.5, sm: 4.5 }}>
                    {!isOtpSent ? (
                      <>
                        <Stack spacing={1}>
                          {renderFieldLabel('Phone Number', true)}
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'minmax(116px, 136px) minmax(0, 1fr)',
                              },
                              gap: 1.5,
                              width: '100%',
                            }}
                          >
                            <TextField
                              select
                              value={sentOtpData.countryCode || ''}
                              onChange={handleCountryChange}
                              error={Boolean(errors.countryCode)}
                              SelectProps={{
                                IconComponent: KeyboardArrowDownRounded,
                                // On the combobox itself, not the wrapper: this is
                                // the element that opens the list, so it is the one
                                // a test has to click.
                                SelectDisplayProps: { 'data-testid': 'login-country-code' },
                                renderValue: (selectedValue) => {
                                  const selectedCountry =
                                    countryData.find(
                                      (country) =>
                                        String(country.phoneCode) === String(selectedValue)
                                    ) || getSelectedCountry();

                                  if (!selectedCountry) {
                                    return selectedValue || '';
                                  }

                                  return (
                                    <CountryPhoneCodeOption
                                      country={selectedCountry}
                                      fallbackLabel={selectedValue}
                                    />
                                  );
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                None
                              </MenuItem>
                              {countryData.map((country) => (
                                <MenuItem key={country.id} value={String(country.phoneCode)}>
                                  <CountryPhoneCodeMenuOption country={country} />
                                </MenuItem>
                              ))}
                              {countryData.length === 0 ? (
                                <MenuItem disabled>No Options</MenuItem>
                              ) : null}
                            </TextField>

                            <TextField
                              name="phoneNumber"
                              placeholder="Enter your phone number"
                              value={sentOtpData.phoneNumber}
                              onChange={handleChangeSentOtp}
                              error={Boolean(errors.phoneNumber)}
                              inputProps={{ 'data-testid': 'login-phone' }}
                            />
                          </Box>

                          {errors.phoneNumber ? (
                            <Typography
                              variant="caption"
                              color="error.main"
                              data-testid="login-phone-error"
                            >
                              {errors.phoneNumber}
                            </Typography>
                          ) : null}
                          {errors.countryCode ? (
                            <Typography
                              variant="caption"
                              color="error.main"
                              data-testid="login-country-code-error"
                            >
                              {errors.countryCode}
                            </Typography>
                          ) : null}
                        </Stack>

                        {metadataFailed ? (
                          <Typography
                            variant="body2"
                            color="error.main"
                            sx={{ mt: 1 }}
                            data-testid="login-metadata-error"
                          >
                            Could not load the country list, so sign-in is unavailable. Please
                            reload the page — if it keeps happening, the service is down.
                          </Typography>
                        ) : null}

                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          sx={{ mt: 1 }}
                          disabled={countryData.length === 0}
                          data-testid="login-send-otp"
                        >
                          {isPinLoginEnabled() ? 'Continue' : 'Send OTP'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                ...fieldGroupStyles,
                                gap: 0.5,
                                width: otpGridWidth,
                                maxWidth: '100%',
                              }}
                            >
                              {renderFieldLabel('OTP', true)}
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: {
                                    xs: 'repeat(4, 56px)',
                                    sm: 'repeat(4, 72px)',
                                  },
                                  gap: { xs: 1.5, sm: 2.5 },
                                  width: otpGridWidth,
                                  maxWidth: '100%',
                                }}
                              >
                                {otpDigits.map((digit, index) => (
                                  <TextField
                                    key={index}
                                    id={`login-otp-${index}`}
                                    value={digit}
                                    onChange={(event) =>
                                      handleOtpChange(index, event.target.value)
                                    }
                                    onKeyDown={(event) => handleOtpKeyDown(index, event)}
                                    error={Boolean(errors.otp)}
                                    inputProps={{
                                      inputMode: 'numeric',
                                      maxLength: 1,
                                      'aria-label': `OTP digit ${index + 1}`,
                                      'data-testid': `login-otp-${index}`,
                                      style: {
                                        textAlign: 'center',
                                        fontSize: '1.5rem',
                                        lineHeight: 1,
                                        padding: 0,
                                      },
                                    }}
                                    sx={{
                                      width: { xs: 56, sm: 72 },
                                      '& .MuiOutlinedInput-root': {
                                        height: { xs: 52, sm: 56 },
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                              {errors.otp ? (
                                <Typography
                                  variant="caption"
                                  color="error.main"
                                  data-testid="login-otp-error"
                                >
                                  {errors.otp}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                width: '100%',
                                color: 'text.primary',
                                textAlign: 'center',
                              }}
                            >
                              Didn&apos;t receive the code?{' '}
                              {resendSeconds > 0 ? (
                                <Box
                                  component="span"
                                  data-testid="login-resend-otp-cooldown"
                                  sx={{ color: 'text.secondary', fontWeight: 700 }}
                                >
                                  Resend OTP in {resendSeconds}s
                                </Box>
                              ) : (
                                <Link
                                  component="button"
                                  type="button"
                                  onClick={handleResendOtp}
                                  underline="none"
                                  data-testid="login-resend-otp"
                                  sx={{
                                    p: 0,
                                    m: 0,
                                    minWidth: 'auto',
                                    border: 0,
                                    background: 'none',
                                    font: 'inherit',
                                    fontSize: 'inherit',
                                    lineHeight: 'inherit',
                                    verticalAlign: 'baseline',
                                    color: '#294190',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      color: '#294190',
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  Resend OTP
                                </Link>
                              )}
                            </Typography>
                          </Box>
                        </Stack>

                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          sx={{ mt: 1 }}
                          data-testid="login-verify-otp"
                        >
                          Verify OTP
                        </Button>
                      </>
                    )}
                  </Stack>
                </form>
                ) : null}
              </Stack>
            </Box>
          </Paper>
        </Box>
        )}
      </SignupShell>

      <Backdrop
        open={loading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(28, 27, 31, 0.24)',
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={Boolean(feedback?.open)}
        autoHideDuration={4000}
        onClose={closeFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={feedback?.severity || 'info'}
          variant="filled"
          onClose={closeFeedback}
          sx={{ width: '100%' }}
          // Severity is part of the id on purpose: "a toast appeared" is not the
          // assertion worth making — a login that reports failure and signs you
          // in anyway would pass that one.
          data-testid={`login-alert-${feedback?.severity || 'info'}`}
        >
          {feedback?.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
